import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaTooth, FaMapMarkerAlt, FaPhone, 
  FaEnvelope, FaFacebookF, FaInstagram, 
  FaTwitter, FaLinkedinIn, FaArrowUp
} from 'react-icons/fa';

const Footer = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = isMobile ? 70 : 100;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const styles = {
    footer: {
      background: '#0A0F1C',
      color: '#ffffff',
      padding: isMobile ? '40px 0 20px' : '60px 0 30px',
      position: 'relative',
      borderTop: '1px solid rgba(200, 169, 126, 0.2)'
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 20px' : '0 40px',
      position: 'relative',
      zIndex: 2
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1.5fr',
      gap: isMobile ? '30px' : '40px',
      marginBottom: isMobile ? '30px' : '40px'
    },
    logoSection: {
      textAlign: isMobile ? 'center' : 'left'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '15px',
      justifyContent: isMobile ? 'center' : 'flex-start'
    },
    logoIcon: {
      fontSize: isMobile ? '35px' : '40px',
      color: '#C8A97E'
    },
    logoText: {
      fontSize: isMobile ? '22px' : '26px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 800,
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    description: {
      color: '#A0A8B8',
      lineHeight: 1.8,
      marginBottom: '20px',
      fontSize: isMobile ? '14px' : '15px',
      textAlign: isMobile ? 'center' : 'left'
    },
    socialLinks: {
      display: 'flex',
      gap: '12px',
      justifyContent: isMobile ? 'center' : 'flex-start',
      flexWrap: 'wrap'
    },
    socialLink: {
      width: isMobile ? '40px' : '45px',
      height: isMobile ? '40px' : '45px',
      background: 'rgba(200, 169, 126, 0.1)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#C8A97E',
      fontSize: isMobile ? '16px' : '18px',
      transition: 'all 0.3s',
      cursor: 'pointer',
      border: '1px solid rgba(200, 169, 126, 0.2)'
    },
    sectionTitle: {
      fontSize: isMobile ? '20px' : '22px',
      color: '#ffffff',
      marginBottom: isMobile ? '15px' : '20px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 700,
      textAlign: isMobile ? 'center' : 'left'
    },
    titleUnderline: {
      display: isMobile ? 'none' : 'block',
      width: '40px',
      height: '3px',
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      marginTop: '5px'
    },
    links: {
      listStyle: 'none',
      textAlign: isMobile ? 'center' : 'left'
    },
    linkItem: {
      marginBottom: '10px'
    },
    link: {
      color: '#A0A8B8',
      textDecoration: 'none',
      fontSize: isMobile ? '14px' : '15px',
      transition: 'color 0.3s',
      cursor: 'pointer',
      display: 'inline-block'
    },
    contactInfo: {
      listStyle: 'none',
      textAlign: isMobile ? 'center' : 'left'
    },
    contactItem: {
      display: 'flex',
      gap: '12px',
      marginBottom: '12px',
      color: '#A0A8B8',
      fontSize: isMobile ? '14px' : '15px',
      justifyContent: isMobile ? 'center' : 'flex-start',
      alignItems: 'center'
    },
    contactIcon: {
      color: '#C8A97E',
      fontSize: '16px',
      flexShrink: 0
    },
    bottomBar: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: '20px',
      borderTop: '1px solid rgba(200, 169, 126, 0.2)',
      color: '#A0A8B8',
      fontSize: isMobile ? '12px' : '14px',
      gap: isMobile ? '10px' : '0',
      textAlign: 'center'
    },
    backToTop: {
      width: isMobile ? '45px' : '50px',
      height: isMobile ? '45px' : '50px',
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#0A0F1C',
      fontSize: isMobile ? '20px' : '22px',
      cursor: 'pointer',
      position: 'fixed',
      bottom: isMobile ? '15px' : '20px',
      right: isMobile ? '15px' : '20px',
      zIndex: 100,
      boxShadow: '0 10px 20px rgba(200, 169, 126, 0.3)',
      border: '2px solid rgba(255, 255, 255, 0.2)'
    }
  };

  return (
    <footer style={styles.footer}>
      <div style={styles.container}>
        <div style={styles.grid}>
          {/* About Section */}
          <div style={styles.logoSection}>
            <div style={styles.logo}>
              <FaTooth style={styles.logoIcon} />
              <span style={styles.logoText}>Maxfax Dental</span>
            </div>
            <p style={styles.description}>
              Providing world-class dental care with precision and compassion in Multan.
            </p>
            <div style={styles.socialLinks}>
              {[FaFacebookF, FaInstagram, FaTwitter, FaLinkedinIn].map((Icon, index) => (
                <motion.a
                  key={index}
                  href="#"
                  style={styles.socialLink}
                  whileHover={{ 
                    scale: 1.1,
                    background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
                    color: '#0A0F1C'
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={styles.sectionTitle}>
              Quick Links
              <div style={styles.titleUnderline}></div>
            </h4>
            <ul style={styles.links}>
              {['Home', 'About', 'Services', 'Contact'].map((item, index) => (
                <motion.li 
                  key={index} 
                  style={styles.linkItem}
                  whileHover={{ x: isMobile ? 0 : 5 }}
                >
                  <a 
                    href={`#${item.toLowerCase()}`} 
                    style={styles.link}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(item.toLowerCase());
                    }}
                  >
                    {item}
                  </a>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 style={styles.sectionTitle}>
              Services
              <div style={styles.titleUnderline}></div>
            </h4>
            <ul style={styles.links}>
              {[
                'Dental Implants',
                'Oral Surgery',
                'Teeth Whitening',
                'Orthodontics'
              ].map((item, index) => (
                <motion.li 
                  key={index} 
                  style={styles.linkItem}
                  whileHover={{ x: isMobile ? 0 : 5 }}
                >
                  <a href="#services" style={styles.link}>{item}</a>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 style={styles.sectionTitle}>
              Contact
              <div style={styles.titleUnderline}></div>
            </h4>
            <ul style={styles.contactInfo}>
              <li style={styles.contactItem}>
                <FaMapMarkerAlt style={styles.contactIcon} />
                <span>Alam Sher Center, Multan</span>
              </li>
              <li style={styles.contactItem}>
                <FaPhone style={styles.contactIcon} />
                <a href="tel:03014484633" style={styles.link}>0301 4484633</a>
              </li>
              <li style={styles.contactItem}>
                <FaEnvelope style={styles.contactIcon} />
                <a href="mailto:info@maxfaxdental.com" style={styles.link}>info@maxfaxdental.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={styles.bottomBar}>
          <p>© 2026 Maxfax Dental Clinic</p>
          <p>Designed for Dr. Muhammad Athar Munevar</p>
          
        </div>
      </div>

      {/* Back to Top Button */}
      <motion.div 
        style={styles.backToTop}
        onClick={scrollToTop}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <FaArrowUp />
      </motion.div>
    </footer>
  );
};

export default Footer;